// src/views/portaria/PortalPortaria.jsx
import { abrirCamera as openCamera, capturarFoto as captureFrame, pararCamera as stopCamera } from "../../utils/camera";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import portariaService  from "../../services/portariaService";
import unidadesService  from "../../services/unidadesService";
import condominosService from "../../services/condominosService";
import useSocket from "../../hooks/useSocket";
import Banner from "../../components/ui/Banner";
import ThemeToggle from "../../components/layout/ThemeToggle";
import ConfirmModal from "../../components/ui/ConfirmModal";
import {
  AvisosPortariaPanel,
  PrestadoresPortariaPanel,
  NotificacoesPortariaPanel,
} from "./PortariaExtras";

const fmtDT = d => { try{ return new Date(d).toLocaleString("pt-BR",{day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}); }catch{return"—";} };
const fmtD  = d => { try{ const s=(d||"").split("T")[0]; return s?new Date(s+"T12:00:00").toLocaleDateString("pt-BR"):"—"; }catch{return"—";} };

const TP_VISITA = ["Visitante","Prestador","Familiar","Entrega","Outro"];
const TP_MORADOR = ["Proprietario","Familiar","Funcionario"];

const EMPTY_ENTRADA = { nmVisitante:"", tpVisita:"Visitante", documento:"", empresa:"", motivoVisita:"", idUnidade:"", observacao:"" };
const EMPTY_ENCOMENDA = { idUnidade:"", descricao:"", remetente:"", transportadora:"", codigoRastreio:"", observacao:"", fotoEncomenda:null };
const EMPTY_MORADOR = { idUnidade:"", nmMorador:"", tpMorador:"Familiar", parentesco:"", cpf:"", telefone:"", observacao:"" };

// ────────────────────────────────────────────────────────────────────────
// UnidadeAutocomplete — input com datalist (busca por bloco / unidade)
// Útil em condomínios grandes onde o <select> é pouco usável.
// ────────────────────────────────────────────────────────────────────────
const fmtUnid = (u) => `${u.unidade}${u.bloco?` / Bl. ${u.bloco}`:""}`;
function UnidadeAutocomplete({
  unidades = [], value = "", onChange, listId,
  placeholder = "Digite a unidade ou bloco…",
  required = false, size = "md",
}) {
  const idToText = React.useCallback((id) => {
    const u = unidades.find(x => String(x.id) === String(id));
    return u ? fmtUnid(u) : "";
  }, [unidades]);
  const [text, setText] = useState(idToText(value));
  useEffect(() => { setText(idToText(value)); }, [value, idToText]);

  const norm = s => (s||"").toString().toLowerCase().replace(/\s+/g, "");
  const handleChange = (e) => {
    const v = e.target.value;
    setText(v);
    const target = norm(v);
    const u = unidades.find(x => norm(fmtUnid(x)) === target);
    onChange(u ? String(u.id) : "");
  };
  const handleBlur = () => {
    // Se o usuário deixou texto que não bate com nenhuma unidade, limpa.
    const target = norm(text);
    if (!text.trim()) { onChange(""); return; }
    const u = unidades.find(x => norm(fmtUnid(x)) === target);
    if (!u) { setText(""); onChange(""); }
  };

  const cls = size === "sm" ? "form-control form-control-sm" : "form-control";
  return (
    <>
      <input className={cls} list={listId} value={text}
        onChange={handleChange} onBlur={handleBlur}
        placeholder={placeholder} required={required}
        autoComplete="off"/>
      <datalist id={listId}>
        {unidades.map(u => <option key={u.id} value={fmtUnid(u)}/>)}
      </datalist>
    </>
  );
}

export default function PortalPortaria({ onLogout }) {
  const { usuario, condominioAtivo } = useAuth();
  const idCondo = String(
    condominioAtivo?.id ||
    usuario?.idCondominio ||
    usuario?.condominio?.id ||
    (usuario?.condominios?.length === 1 ? usuario.condominios[0].id : "") ||
    ""
  );
  // Perfis com poder de excluir registros sem limite de tempo
  const PERFIL_ADMIN = ["Admin","SuperAdmin","Sindico","Subsindico"];
  const isAdminUser  = PERFIL_ADMIN.includes(usuario?.perfil) ||
                       usuario?.isSuperAdmin === true || usuario?.isSuperAdmin === 1;
  // Verifica se o porteiro atual pode excluir um registro:
  //  • Admin/Sindico/SuperAdmin: sempre
  //  • Porteiro: somente se ele criou e está nos primeiros 15 min
  const isPortariaOp = ["Porteiro", "Portaria"].includes(usuario?.perfil);
  const podeExcluirReg = (reg) => {
    if (!reg || reg.dtSaida) return false;
    if (isAdminUser) return true;
    if (!isPortariaOp) return false;
    if (Number(reg.idPorteiro) !== Number(usuario?.id)) return false;
    const idadeMin = (Date.now() - new Date(reg.dtEntrada).getTime()) / 60000;
    return idadeMin <= 15;
  };
  const [aba, setAba]             = useState("inicio");
  const [dashboard, setDashboard] = useState({});
  const [registros, setRegistros] = useState([]);
  const [encomendas, setEncomendas]     = useState([]);
  const [encStatus,   setEncStatus]     = useState("");
  const [encUnidade,  setEncUnidade]    = useState("");
  const [editEnc,     setEditEnc]       = useState(null);
  const [editReg,     setEditReg]       = useState(null);
  const [moradores, setMoradores]     = useState([]);
  const [titulares, setTitulares]       = useState([]);
  const [expandedUnits, setExpandedUnits] = useState({});
  const [unidades, setUnidades]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);

  // UX: banner + modais de confirmação / motivo
  const [banner, setBanner] = useState(null); // { tipo, msg }
  const [confirmacao, setConfirmacao] = useState(null); // { titulo, mensagem, onConfirm, perigo, textoOk }
  const [motivoModal, setMotivoModal] = useState(null); // { titulo, label, textoOk }
  const [motivoTexto, setMotivoTexto] = useState("");
  const motivoResolveRef = useRef(null);
  const notify = useCallback((tipo, msg) => {
    setBanner({ tipo, msg });
    if (tipo === "success" || tipo === "info") {
      setTimeout(() => setBanner((b) => (b && b.msg === msg ? null : b)), 4000);
    }
  }, []);
  const askConfirm = useCallback(
    (cfg) => new Promise((resolve) => {
      setConfirmacao({
        titulo: cfg.titulo || "Confirmar",
        mensagem: cfg.mensagem,
        perigo: !!cfg.perigo,
        textoOk: cfg.textoOk || "Confirmar",
        onConfirm: () => { setConfirmacao(null); resolve(true); },
        onCancel:  () => { setConfirmacao(null); resolve(false); },
      });
    }),
    []
  );
  const askMotivo = useCallback(
    (cfg) => new Promise((resolve) => {
      setMotivoTexto("");
      motivoResolveRef.current = resolve;
      setMotivoModal({
        titulo: cfg.titulo || "Motivo",
        label: cfg.label || "Informe o motivo",
        textoOk: cfg.textoOk || "Confirmar",
      });
    }),
    []
  );
  const fecharMotivo = useCallback((texto) => {
    setMotivoModal(null);
    const r = motivoResolveRef.current;
    motivoResolveRef.current = null;
    setMotivoTexto("");
    if (r) r(texto);
  }, []);

  // Modais
  const [showEntrada,  setShowEntrada]  = useState(false);
  const [showEncomenda,setShowEncomenda]= useState(false);
  const [showRetirada, setShowRetirada] = useState(false);
  const [showMorador,  setShowMorador]  = useState(false);
  const [editMorador,  setEditMorador]  = useState(null);
  const [retiradaItem, setRetiradaItem] = useState(null);

  const [formEntrada,   setFE] = useState(EMPTY_ENTRADA);
  const [formEncomenda, setFEnc] = useState(EMPTY_ENCOMENDA);
  const [formRetirada,  setFR] = useState({nmRetiradoPor:"", fotoRetirada:null});
  const [formMorador,   setFM] = useState(EMPTY_MORADOR);
  const [showVeicEnt,  setShowVeicEnt]  = useState(false);
  const [formVeicEnt,  setFormVeicEnt]  = useState({ placa:"", nmMotorista:"", tpAcesso:"Visitante" });
  const [recogVeic,    setRecogVeic]    = useState({ status:"idle", veiculo:null });

  const [busca, setBusca]       = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroEntBusca, setFiltroEntBusca] = useState("");
  const [filtroEntDtIni, setFiltroEntDtIni] = useState("");
  const [filtroEntDtFim, setFiltroEntDtFim] = useState("");
  // Paginação Entradas/Saídas
  const [pageEnt, setPageEnt]   = useState(1);
  const [totalEnt, setTotalEnt] = useState(0);
  const PAGE_SIZE_ENT = 20;
  const totalPagesEnt = Math.max(1, Math.ceil(totalEnt / PAGE_SIZE_ENT));

  const videoRef = useRef(null);
  const [cameraAtiva, setCameraAtiva] = useState(null); // "encomenda" | "retirada"

  // Real-time
  useSocket(idCondo, {
    visita_chegou: () => carregarDashboard(),
    encomenda_chegou: () => carregarDashboard(),
    aviso_portaria_novo: () => carregarDashboard(),
  });

  const abrirEntradaPrestador = useCallback((dados) => {
    setFE({ ...EMPTY_ENTRADA, ...dados });
    setShowEntrada(true);
  }, []);

  const carregarDashboard = useCallback(async () => {
    if (!idCondo) return;
    try {
      const r = await portariaService.dashboard({idCondominio:idCondo});
      setDashboard(r.data||{});
    } catch{}
  }, [idCondo]);

  const carregarUnidades = useCallback(async () => {
    if (!idCondo) return;
    try {
      const r = await unidadesService.listar({idCondominio:idCondo, limit:500});
      setUnidades(r.data||[]);
    } catch{}
  }, [idCondo]);

  useEffect(() => { carregarDashboard(); carregarUnidades(); }, [carregarDashboard, carregarUnidades]);

  // ── Reconhecimento de placa no modal de Entrada de Veículo ────────────────
  // Debounce: 400ms após o usuário parar de digitar, consulta o backend.
  useEffect(() => {
    if (!showVeicEnt) { setRecogVeic({ status:"idle", veiculo:null }); return; }
    const placaLimpa = (formVeicEnt.placa || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (placaLimpa.length < 4) { setRecogVeic({ status:"idle", veiculo:null }); return; }
    setRecogVeic({ status:"searching", veiculo:null });
    const t = setTimeout(async () => {
      try {
        const r = await portariaService.veiculos({ idCondominio: idCondo, placa: placaLimpa });
        const lista = r?.data || [];
        if (lista.length > 0) {
          const v = lista[0];
          setRecogVeic({ status:"found", veiculo:v });
          setFormVeicEnt(p => ({
            ...p,
            tpAcesso: "Morador",
            nmMotorista: p.nmMotorista || v.nmProprietario || "",
          }));
        } else {
          setRecogVeic({ status:"not_found", veiculo:null });
          setFormVeicEnt(p => ({ ...p, tpAcesso: p.tpAcesso === "Morador" ? "Visitante" : p.tpAcesso }));
        }
      } catch { setRecogVeic({ status:"idle", veiculo:null }); }
    }, 400);
    return () => clearTimeout(t);
  }, [formVeicEnt.placa, showVeicEnt, idCondo]);

  const carregarAba = useCallback(async (novaAba) => {
    setAba(novaAba);
    setLoading(true);
    try {
      if (novaAba==="entradas") {
        const r = await portariaService.registros({
          idCondominio: idCondo,
          status:       filtroStatus,
          search:       filtroEntBusca,
          dtIni:        filtroEntDtIni,
          dtFim:        filtroEntDtFim,
          page:         pageEnt,
          limit:        PAGE_SIZE_ENT,
        });
        setRegistros(r.data || []);
        setTotalEnt(Number(r.total ?? r.meta?.total ?? r.data?.length ?? 0));
      }
      if (novaAba==="encomendas") {
        const r = await portariaService.encomendas({idCondominio:idCondo, status:encStatus, idUnidade:encUnidade});
        setEncomendas(r.data||[]);
      }
      if (novaAba==="moradores") {
        const r = await portariaService.moradores({idCondominio:idCondo, search:busca});
        setMoradores(r.data||[]);
        setTitulares(r.titulares||[]);
        if (!busca) setExpandedUnits({});
      }
      if (novaAba==="veiculos") {
        const [rv,rr] = await Promise.all([
          portariaService.veiculos({idCondominio:idCondo}),
          portariaService.veiculosRegistros({idCondominio:idCondo}),
        ]);
        setVeiculos(rv.data||[]); setVeicRegis(rr.data||[]);
      }
      if (novaAba==="preauth") {
        const r = await portariaService.preautorizacoes({idCondominio:idCondo});
        setPreauth(r.data||[]);
      }
      if (novaAba==="ocorrencias") {
        const r = await portariaService.ocorrenciasPortaria({idCondominio:idCondo});
        setOcorrPort(r.data||[]);
      }
      if (novaAba==="scanner") { setScanResult(null); setScanInput(""); }
      if (novaAba==="relatorio") {
        const r = await portariaService.relatorio({idCondominio:idCondo,dtIni:relDtIni,dtFim:relDtFim});
        setRelatorio(r.data);
      }
    } catch(e){ console.error(e); }
    setLoading(false);
  }, [idCondo, filtroStatus, busca, filtroEntBusca, filtroEntDtIni, filtroEntDtFim, pageEnt]);

  // Recarrega Entradas/Saídas ao trocar de página (apenas pageEnt — outras deps são intencionalmente omitidas).
  useEffect(() => {
    if (aba === "entradas") carregarAba("entradas");
  }, [pageEnt]); // eslint-disable-line

  // Camera helpers
  const abrirCamera = async (tipo) => {
    setCameraAtiva(tipo);
    try {
      const stream = await openCamera();
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch(e) {
      const msg = e.name === "NotAllowedError"
        ? "Permissão de câmera negada. Clique no cadeado na barra de endereço e permita o acesso."
        : e.name === "NotFoundError" || e.message.includes("device not found")
        ? "Nenhuma câmera encontrada neste dispositivo."
        : "Câmera não disponível: " + e.message;
      notify("error", msg);
      setCameraAtiva(null);
    }
  };

  const capturarFoto = () => {
    const canvas = document.createElement("canvas");
    const video  = videoRef.current;
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const b64 = canvas.toDataURL("image/jpeg", 0.7);
    if (cameraAtiva==="encomenda") setFEnc(p=>({...p, fotoEncomenda:b64}));
    if (cameraAtiva==="retirada")  setFR(p=>({...p, fotoRetirada:b64}));
    video.srcObject?.getTracks().forEach(t=>t.stop());
    setCameraAtiva(null);
  };

  const handleRegistrarEntrada = async () => {
    if (!formEntrada.nmVisitante.trim()) return notify("warning","Informe o nome do visitante.");
    setSaving(true);
    try {
      await portariaService.registrarEntrada({...formEntrada, idCondominio:idCondo});
      setShowEntrada(false); setFE(EMPTY_ENTRADA);
      carregarDashboard();
      if (aba==="entradas") carregarAba("entradas");
      notify("success", "Entrada registrada com sucesso.");
    } catch(e){ notify("error", e.message); }
    setSaving(false);
  };

  const handleRegistrarSaida = async (id) => {
    const ok = await askConfirm({ titulo:"Registrar saída", mensagem:"Confirmar saída deste visitante?", textoOk:"Confirmar" });
    if (!ok) return;
    try {
      await portariaService.registrarSaida(id);
      setRegistros(rs=>rs.map(r=>r.id===id?{...r,dtSaida:new Date().toISOString()}:r));
      carregarDashboard();
      notify("success", "Saída registrada.");
    } catch(e){ notify("error", e.message); }
  };

  const handleExcluirRegistro = async (reg) => {
    if (!reg) return;
    const idadeMin = Math.floor((Date.now() - new Date(reg.dtEntrada).getTime()) / 60000);
    const mensagem = isAdminUser
      ? `Excluir o registro de "${reg.nmVisitante}"? Esta ação é registrada na auditoria.`
      : `Excluir o registro de "${reg.nmVisitante}"? Registros só podem ser excluídos nos primeiros 15 minutos (atual: ${idadeMin} min).`;
    const ok = await askConfirm({
      titulo: "Excluir registro",
      mensagem,
      perigo: true,
      textoOk: "Excluir",
    });
    if (!ok) return;
    const motivo = await askMotivo({
      titulo: "Motivo da exclusão",
      label: "Informe o motivo (opcional)",
      textoOk: "Excluir",
    });
    if (motivo === null) return; // cancelou
    try {
      await portariaService.excluirRegistro(reg.id, { motivo: motivo || "" });
      setRegistros(rs => rs.filter(r => r.id !== reg.id));
      carregarDashboard();
      notify("success", "Registro excluído.");
    } catch (e) { notify("error", e.message); }
  };

  const handleRegistrarEncomenda = async () => {
    if (!formEncomenda.idUnidade || !formEncomenda.descricao.trim()) return notify("warning","Unidade e descrição são obrigatórios.");
    setSaving(true);
    try {
      await portariaService.registrarEncomenda({...formEncomenda, idCondominio:idCondo});
      setShowEncomenda(false); setFEnc(EMPTY_ENCOMENDA);
      carregarDashboard();
      if (aba==="encomendas") carregarAba("encomendas");
      notify("success", "Encomenda registrada.");
    } catch(e){ notify("error", e.message); }
    setSaving(false);
  };

  const abrirRetirada = async (encomenda) => {
    setRetiradaItem(encomenda);
    setFR({ nmRetiradoPor:"", fotoRetirada:null });
    setShowRetirada(true);
    // Carrega moradores da unidade para seleção rápida
    if (encomenda?.idUnidade && idCondo) {
      try {
        const r = await portariaService.moradores({ idCondominio:idCondo, idUnidade:encomenda.idUnidade });
        setMoradores(r.data||[]);
      } catch {}
    }
  };

  const handleRetirada = async () => {
    if (!formRetirada.nmRetiradoPor.trim()) return notify("warning","Informe o nome de quem está retirando.");
    setSaving(true);
    try {
      await portariaService.registrarRetirada(retiradaItem.id, formRetirada);
      setShowRetirada(false); setFR({nmRetiradoPor:"",fotoRetirada:null}); setRetiradaItem(null);
      carregarAba("encomendas");
      notify("success", "Retirada registrada.");
    } catch(e){ notify("error", e.message); }
    setSaving(false);
  };

  const handleSalvarMorador = async () => {
    if (!formMorador.idUnidade || !formMorador.nmMorador.trim()) return notify("warning","Unidade e nome são obrigatórios.");
    setSaving(true);
    try {
      if (editMorador?.id) await portariaService.atualizarMorador(editMorador.id,{...formMorador,idCondominio:idCondo});
      else                 await portariaService.criarMorador({...formMorador,idCondominio:idCondo});
      setShowMorador(false); setFM(EMPTY_MORADOR); setEditMorador(null);
      carregarAba("moradores");
      notify("success", editMorador?.id ? "Morador atualizado." : "Morador cadastrado.");
    } catch(e){ notify("error", e.message); }
    setSaving(false);
  };

  const [veiculos,     setVeiculos]     = useState([]);
  const [buscaVeic,    setBuscaVeic]    = useState(""); // search for vehicles
  const [expandedUnitsVeic, setExpandedUnitsVeic] = useState({});
  const [veicRegis,    setVeicRegis]    = useState([]);
  const [preauth,      setPreauth]      = useState([]);
  const [ocorrPortaria,setOcorrPort]    = useState([]);
  const [relatorio,    setRelatorio]    = useState(null);
  const [showOcorr,    setShowOcorr]    = useState(false);
  const [formOcorr,    setFormOcorr]    = useState({tpOcorrencia:"Outro",descricao:"",local:"",gravidade:"Media",idUnidade:""});
  const [editOcorr,    setEditOcorr]    = useState(null);
  const [relDtIni,     setRelDtIni]     = useState(new Date().toISOString().split("T")[0]);
  const [showScanner,  setShowScanner]  = useState(false);
  const [scanResult,   setScanResult]   = useState(null);
  const [scanLoading,  setScanLoading]  = useState(false);
  const [scanInput,    setScanInput]    = useState("");
  const [relDtFim,     setRelDtFim]     = useState(new Date().toISOString().split("T")[0]);


  const validarToken = async (token) => {
    setScanLoading(true); setScanResult(null);
    try {
      const r = await portariaService.validarQR(token);
      setScanResult(r);
    } catch(e) { setScanResult({autorizado:false, motivo:e.message}); }
    setScanLoading(false);
  };

  const streamRef = useRef(null);

  const iniciarScanner = async () => {
    setShowScanner(true);
    setScanResult(null);
    try {
      const stream = await openCamera();
      streamRef.current = stream;
      setTimeout(() => {
        const video = document.getElementById("qr-video");
        if (video) { video.srcObject = stream; video.play(); }
      }, 100);
      // Poll for QR using BarcodeDetector if available
      if ("BarcodeDetector" in window) {
        const detector = new window.BarcodeDetector({ formats:["qr_code"] });
        const scan = async () => {
          const video = document.getElementById("qr-video");
          if (!video || !streamRef.current) return;
          try {
            const codes = await detector.detect(video);
            if (codes.length > 0) {
              const token = codes[0].rawValue;
              pararScanner(); setShowScanner(false);
              setScanInput(token);
              validarToken(token);
              return;
            }
          } catch(e) {}
          if (streamRef.current) setTimeout(scan, 400);
        };
        setTimeout(scan, 500);
      }
    } catch(e) {
      setShowScanner(false);
      notify("error", "Câmera não disponível: " + e.message);
    }
  };

  const pararScanner = () => {
    if (streamRef.current) {
      stopCamera(streamRef.current);
      streamRef.current = null;
    }
  };

  const COR_TP = { Visitante:"#1D4ED8", Prestador:"#7C3AED", Familiar:"#166534", Entrega:"#C2410C", Outro:"#64748B" };
  const BG_TP  = { Visitante:"#EFF6FF", Prestador:"#F5F3FF", Familiar:"#DCFCE7", Entrega:"#FFF7ED", Outro:"#F1F5F9" };

  return (
    <div className="portaria-page" style={{display:"flex",flexDirection:"column"}}>

      {/* Header */}
      <div style={{background:"#1B3A6B",padding:"0 20px",display:"flex",alignItems:"center",
        justifyContent:"space-between",height:60,flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <i className="bi bi-shield-check" style={{color:"#fff",fontSize:22}}/>
          <div>
            <span style={{color:"#fff",fontWeight:700,fontSize:15}}>eCondo</span>
            <span style={{color:"rgba(255,255,255,.6)",fontSize:12,marginLeft:8}}>Portaria</span>
          </div>
          {condominioAtivo && (
            <span style={{background:"rgba(255,255,255,.15)",color:"rgba(255,255,255,.9)",
              fontSize:12,padding:"3px 10px",borderRadius:12,marginLeft:8}}>
              {condominioAtivo.nmFantasia||condominioAtivo.nmCondominio}
            </span>
          )}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <ThemeToggle variant="onDark" />
          <span style={{color:"rgba(255,255,255,.8)",fontSize:13}}>
            <i className="bi bi-person-badge me-1"/>{usuario?.nome?.split(" ")[0]}
          </span>
          <button onClick={onLogout}
            style={{background:"rgba(255,255,255,.15)",border:"none",color:"#fff",
              borderRadius:6,padding:"4px 12px",fontSize:12,cursor:"pointer"}}>
            <i className="bi bi-box-arrow-right me-1"/>Sair
          </button>
        </div>
      </div>

      {/* Nav */}
      <div style={{background:"var(--bg-surface)",borderBottom:"1px solid var(--border-color)",
        padding:"0 16px",display:"flex",gap:0,overflowX:"auto"}}>
        {[
          {id:"inicio",    icon:"bi-speedometer2",   label:"Início"},
          {id:"entradas",  icon:"bi-door-open",       label:`Entradas/Saídas${dashboard.dentroAgora>0?` (${dashboard.dentroAgora})`:""}` },
          {id:"encomendas",icon:"bi-box-seam",        label:`Encomendas${dashboard.encomendasAguard>0?` (${dashboard.encomendasAguard})`:""}` },
          {id:"moradores",  icon:"bi-people-fill",         label:"Moradores"},
          {id:"veiculos",   icon:"bi-car-front",           label:`Veículos${dashboard.veiculosDentro>0?` (${dashboard.veiculosDentro})`:""}` },
          {id:"preauth",    icon:"bi-qr-code-scan",        label:`Pré-autorizados${(dashboard.preauthHoje||preauth.length)>0?` (${dashboard.preauthHoje||preauth.length})`:""}` },
          {id:"ocorrencias",icon:"bi-exclamation-triangle",label:`Ocorrências${dashboard.ocorrenciasAbertas>0?` (${dashboard.ocorrenciasAbertas})`:""}` },
          {id:"avisos",     icon:"bi-megaphone-fill",      label:`Avisos${dashboard.avisosNovos>0?` (${dashboard.avisosNovos})`:""}` },
          {id:"prestadores",icon:"bi-tools",             label:"Prestadores"},
          {id:"notificacoes",icon:"bi-bell",              label:"Notificações"},
          {id:"scanner",    icon:"bi-qr-code-scan",        label:"Scanner QR"},
          {id:"relatorio",  icon:"bi-bar-chart",           label:"Relatório"},
        ].map(t=>(
          <button key={t.id} onClick={()=>carregarAba(t.id)}
            style={{border:"none",background:"none",padding:"14px 16px",cursor:"pointer",
              color:aba===t.id?"#1B3A6B":"#64748B",fontWeight:aba===t.id?700:400,
              borderBottom:aba===t.id?"3px solid #1B3A6B":"3px solid transparent",
              fontSize:13,display:"flex",alignItems:"center",gap:6,whiteSpace:"nowrap"}}>
            <i className={`bi ${t.icon}`}/> {t.label}
          </button>
        ))}
      </div>

      <div style={{flex:1,padding:20,maxWidth:1000,width:"100%",margin:"0 auto",overflowY:"auto",alignSelf:"flex-start"}}>

        {/* ── INÍCIO ── */}
        {aba==="inicio" && (
          <div>
            <h2 style={{fontSize:18,fontWeight:700,color:"var(--text-primary)",marginBottom:20}}>
              Painel da Portaria
            </h2>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:16,marginBottom:24}}>
              {[
                {label:"Pessoas dentro",   value:dashboard.dentroAgora||0,      icon:"bi-people",          color:"#166534",bg:"#DCFCE7"},
                {label:"Entradas hoje",    value:dashboard.entradasHoje||0,     icon:"bi-door-open",        color:"#1D4ED8",bg:"#EFF6FF"},
                {label:"Encomendas aguardando",value:dashboard.encomendasAguard||0,icon:"bi-box-seam",     color:"#C2410C",bg:"#FFF7ED"},
                {label:"Avisos novos",       value:dashboard.avisosNovos||0,      icon:"bi-megaphone-fill",color:"#B45309",bg:"#FEF9C3",aba:"avisos"},
              ].map(card=>(
                <div key={card.label} className="card" style={{padding:20, cursor: card.aba ? "pointer" : undefined}}
                  onClick={card.aba ? () => carregarAba(card.aba) : undefined}>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    <div style={{width:44,height:44,borderRadius:10,background:card.bg,
                      display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <i className={`bi ${card.icon}`} style={{fontSize:20,color:card.color}}/>
                    </div>
                    <div>
                      <div style={{fontSize:26,fontWeight:700,color:"var(--text-primary)"}}>{card.value}</div>
                      <div style={{fontSize:12,color:"var(--gray-400)"}}>{card.label}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Ações rápidas */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:12,marginBottom:24}}>
              <button onClick={()=>setShowEntrada(true)} className="btn btn-primary"
                style={{padding:"16px",fontSize:15,fontWeight:700,borderRadius:12,
                  display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
                <i className="bi bi-person-plus" style={{fontSize:28}}/>
                Registrar Entrada
              </button>
              <button onClick={()=>{setShowEncomenda(true); carregarUnidades();}}
                className="btn btn-outline-primary"
                style={{padding:"16px",fontSize:15,fontWeight:700,borderRadius:12,
                  display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
                <i className="bi bi-box-seam" style={{fontSize:28}}/>
                Nova Encomenda
              </button>
            </div>

            {/* Quem está dentro */}
            {(dashboard.ultimasEntradas||[]).length > 0 && (
              <div className="card" style={{padding:0,overflow:"hidden"}}>
                <div style={{padding:"12px 16px",background:"var(--bg-surface-alt)",
                  borderBottom:"1px solid var(--border-color)",fontWeight:700,fontSize:13}}>
                  <i className="bi bi-people me-2"/>Dentro do condomínio agora
                </div>
                {(dashboard.ultimasEntradas||[]).map(r=>(
                  <div key={r.id} style={{padding:"12px 16px",display:"flex",
                    justifyContent:"space-between",alignItems:"center",
                    borderBottom:"1px solid var(--border-subtle)"}}>
                    <div>
                      <div style={{fontWeight:600,fontSize:14}}>{r.nmVisitante}</div>
                      <div style={{fontSize:12,color:"var(--gray-400)"}}>
                        {r.tpVisita} · Unidade {r.nmUnidade||"?"} · Entrada {fmtDT(r.dtEntrada)}
                      </div>
                    </div>
                    <button className="btn btn-outline-danger btn-sm"
                      onClick={()=>handleRegistrarSaida(r.id)}>
                      <i className="bi bi-door-closed me-1"/>Saída
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── ENTRADAS/SAÍDAS ── */}
        {aba==="entradas" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:10}}>
              <h3 style={{margin:0,fontSize:16,fontWeight:700}}>Registros de Entrada/Saída</h3>
              <button className="btn btn-primary btn-sm" onClick={()=>setShowEntrada(true)}>
                <i className="bi bi-person-plus me-1"/>Registrar Entrada
              </button>
            </div>
            <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
              <div style={{position:"relative",flex:1,minWidth:200}}>
                <i className="bi bi-search" style={{position:"absolute",left:10,top:"50%",
                  transform:"translateY(-50%)",color:"var(--gray-400)",fontSize:13}}/>
                <input className="form-control form-control-sm" style={{paddingLeft:32}}
                  placeholder="Buscar por nome, doc., empresa ou unidade..."
                  value={filtroEntBusca}
                  onChange={e=>{
                    setFiltroEntBusca(e.target.value);
                    if (e.target.value.length === 0) { setPageEnt(1); carregarAba("entradas"); }
                  }}
                  onKeyDown={e=>e.key==="Enter"&&(setPageEnt(1), carregarAba("entradas"))}/>
              </div>
              <select className="form-select form-select-sm" style={{width:"auto"}}
                value={filtroStatus} onChange={e=>{setFiltroStatus(e.target.value); setPageEnt(1); carregarAba("entradas");}}>
                <option value="">Todos</option>
                <option value="dentro">Dentro agora</option>
                <option value="saiu">Já saíram</option>
              </select>
              <input type="date" className="form-control form-control-sm" style={{width:140}}
                value={filtroEntDtIni} onChange={e=>setFiltroEntDtIni(e.target.value)}
                title="Data inicial"/>
              <input type="date" className="form-control form-control-sm" style={{width:140}}
                value={filtroEntDtFim} onChange={e=>setFiltroEntDtFim(e.target.value)}
                title="Data final"/>
              <button className="btn btn-primary btn-sm" onClick={()=>{setPageEnt(1); carregarAba("entradas");}}>
                <i className="bi bi-funnel me-1"/>Aplicar
              </button>
              {(filtroEntBusca||filtroEntDtIni||filtroEntDtFim||filtroStatus) && (
                <button className="btn btn-outline-secondary btn-sm"
                  onClick={()=>{
                    setFiltroEntBusca(""); setFiltroEntDtIni(""); setFiltroEntDtFim(""); setFiltroStatus("");
                    setPageEnt(1);
                    setTimeout(()=>carregarAba("entradas"), 0);
                  }}>
                  <i className="bi bi-x"/>
                </button>
              )}
            </div>
            {loading ? <div style={{textAlign:"center",padding:40}}><span className="spinner-border text-primary"/></div>
            : registros.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-door-open" style={{fontSize:40,display:"block",marginBottom:8}}/>
                Nenhum registro encontrado
              </div>
            ) : (
              <>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {registros.map(r=>(
                  <div key={r.id} className="card" style={{padding:"12px 16px",
                    borderLeft:`4px solid ${r.dtSaida?"#94A3B8":COR_TP[r.tpVisita]||"#1B3A6B"}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontWeight:700,fontSize:14}}>{r.nmVisitante}</span>
                          <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:10,
                            background:BG_TP[r.tpVisita]||"#F1F5F9",
                            color:COR_TP[r.tpVisita]||"#64748B"}}>{r.tpVisita}</span>
                          {!r.dtSaida && <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",
                            borderRadius:10,background:"var(--bg-success-soft)",color:"#166534"}}>● Dentro</span>}
                        </div>
                        <div style={{fontSize:12,color:"var(--gray-400)",marginTop:2}}>
                          {r.nmUnidade && `Unidade ${r.nmUnidade} · `}
                          Entrada: {fmtDT(r.dtEntrada)}
                          {r.dtSaida && ` · Saída: ${fmtDT(r.dtSaida)}`}
                          {r.empresa && ` · ${r.empresa}`}
                        </div>
                      </div>
                      {!r.dtSaida && (
                        <div style={{display:"flex",gap:6}}>
                          <button className="btn btn-outline-secondary btn-sm"
                            title="Editar registro"
                            onClick={()=>setEditReg({...r})}>
                            <i className="bi bi-pencil"/>
                          </button>
                          {podeExcluirReg(r) && (
                            <button className="btn btn-outline-danger btn-sm"
                              title={isAdminUser ? "Excluir registro" : "Excluir (15 min)"}
                              onClick={()=>handleExcluirRegistro(r)}>
                              <i className="bi bi-trash"/>
                            </button>
                          )}
                          <button className="btn btn-success btn-sm"
                            onClick={()=>handleRegistrarSaida(r.id)}>
                            <i className="bi bi-door-closed me-1"/>Registrar Saída
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Paginação Entradas/Saídas */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                marginTop:14,padding:"10px 4px",borderTop:"1px solid var(--border-color)",flexWrap:"wrap",gap:8}}>
                <div style={{fontSize:12,color:"var(--gray-500)"}}>
                  Mostrando <strong>{(pageEnt-1)*PAGE_SIZE_ENT + 1}</strong>–
                  <strong>{Math.min(pageEnt*PAGE_SIZE_ENT, totalEnt)}</strong> de <strong>{totalEnt}</strong> registro{totalEnt===1?"":"s"}
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <button className="btn btn-outline-secondary btn-sm"
                    disabled={pageEnt<=1 || loading}
                    onClick={()=>setPageEnt(p=>Math.max(1,p-1))}>
                    <i className="bi bi-chevron-left"/> Anterior
                  </button>
                  <span style={{fontSize:12,color:"var(--gray-500)",padding:"0 8px"}}>
                    Página <strong>{pageEnt}</strong> de <strong>{totalPagesEnt}</strong>
                  </span>
                  <button className="btn btn-outline-secondary btn-sm"
                    disabled={pageEnt>=totalPagesEnt || loading}
                    onClick={()=>setPageEnt(p=>Math.min(totalPagesEnt,p+1))}>
                    Próximo <i className="bi bi-chevron-right"/>
                  </button>
                </div>
              </div>
              </>
            )}
          </div>
        )}

        {/* ── ENCOMENDAS ── */}
        {aba==="encomendas" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,gap:10,flexWrap:"wrap"}}>
              <h3 style={{margin:0,fontSize:16,fontWeight:700}}>Encomendas ({encomendas.length})</h3>
              <button className="btn btn-primary btn-sm" onClick={()=>setShowEncomenda(true)}>
                <i className="bi bi-plus me-1"/>Nova Encomenda
              </button>
            </div>
            {/* Filtros */}
            <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
              {["","Aguardando","Retirada"].map(s=>(
                <button key={s} type="button"
                  className={`btn btn-sm ${encStatus===s?"btn-primary":"btn-outline-secondary"}`}
                  onClick={()=>{ setEncStatus(s); portariaService.encomendas({idCondominio:idCondo,status:s,idUnidade:encUnidade}).then(r=>setEncomendas(r.data||[])); }}>
                  {s===""?"Todas":s}
                </button>
              ))}
              <select className="form-select form-select-sm" style={{width:190}}
                value={encUnidade}
                onChange={e=>{ setEncUnidade(e.target.value); portariaService.encomendas({idCondominio:idCondo,status:encStatus,idUnidade:e.target.value}).then(r=>setEncomendas(r.data||[])); }}>
                <option value="">Todas as unidades</option>
                {unidades.map(u=><option key={u.id} value={u.id}>Ap. {u.unidade}{u.bloco?`/Bl.${u.bloco}`:""}</option>)}
              </select>
            </div>
            {loading ? <div style={{textAlign:"center",padding:40}}><span className="spinner-border text-primary"/></div>
            : encomendas.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-box-seam" style={{fontSize:40,display:"block",marginBottom:8}}/>
                Nenhuma encomenda aguardando retirada
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {encomendas.map(e=>(
                  <div key={e.id} className="card" style={{padding:16}}>
                    <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                      <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
                        {e.fotoEncomenda && (
                          <img src={e.fotoEncomenda} alt="encomenda"
                            style={{width:64,height:64,borderRadius:8,objectFit:"cover",flexShrink:0}}/>
                        )}
                        <div>
                          <div style={{fontWeight:700,fontSize:14}}>{e.descricao}</div>
                          <div style={{fontSize:12,color:"var(--gray-400)",marginTop:2}}>
                            Unidade {e.nmUnidade}{e.bloco?` / Bl.${e.bloco}`:""} · {e.nmCondominoUnidade||""}
                          </div>
                          <div style={{fontSize:12,color:"var(--gray-400)"}}>
                            Chegou: {fmtDT(e.dtChegada)}
                            {e.remetente && ` · De: ${e.remetente}`}
                            {e.transportadora && ` · ${e.transportadora}`}
                          </div>
                        </div>
                      </div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {e.status==="Aguardando" && (<>
                          <button className="btn btn-outline-secondary btn-sm"
                            onClick={()=>setEditEnc({...e})}>
                            <i className="bi bi-pencil"/>
                          </button>
                          <button className="btn btn-outline-danger btn-sm"
                            onClick={async()=>{
                              const ok = await askConfirm({
                                titulo:"Excluir encomenda",
                                mensagem:"Excluir esta encomenda? A ação não pode ser desfeita.",
                                perigo:true, textoOk:"Excluir",
                              });
                              if(!ok) return;
                              try {
                                await portariaService.excluirEncomenda(e.id);
                                carregarAba("encomendas");
                                notify("success","Encomenda excluída.");
                              } catch(err){ notify("error", err.message); }
                            }}>
                            <i className="bi bi-trash3"/>
                          </button>
                          <button className="btn btn-success btn-sm"
                            onClick={()=>{setRetiradaItem(e);setShowRetirada(true);}}>
                            <i className="bi bi-check2-circle me-1"/>Registrar Retirada
                          </button>
                        </>)}
                        {e.status==="Retirada" && (
                          <span style={{fontSize:11,color:"#16A34A",fontWeight:700,
                            padding:"4px 10px",background:"var(--bg-success-soft)",borderRadius:20,border:"1px solid #BBF7D0"}}>
                            ✅ Retirada por: {e.nmRetiradoPor}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── MORADORES ── */}
        {aba==="moradores" && (
          <div>
            <div style={{marginBottom:14}}>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <div style={{position:"relative",flex:1,minWidth:200}}>
                  <i className="bi bi-search" style={{position:"absolute",left:10,top:"50%",
                    transform:"translateY(-50%)",color:"var(--gray-400)",fontSize:14}}/>
                  <input className="form-control" style={{paddingLeft:34,fontSize:13}}
                    placeholder="Buscar por nome, unidade ou bloco..."
                    value={busca}
                    onChange={e=>{
                      setBusca(e.target.value);
                      if (e.target.value.length === 0) carregarAba("moradores");
                    }}
                    onKeyDown={e=>e.key==="Enter"&&carregarAba("moradores")}/>
                </div>
                <button className="btn btn-primary btn-sm" onClick={()=>carregarAba("moradores")}>
                  <i className="bi bi-search me-1"/>Buscar
                </button>
                {busca && <button className="btn btn-outline-secondary btn-sm"
                  onClick={()=>{setBusca("");carregarAba("moradores");}}>
                  <i className="bi bi-x me-1"/>Limpar
                </button>}
              </div>
              <small style={{fontSize:11,color:"var(--gray-400)",marginTop:6,display:"block"}}>
                <i className="bi bi-info-circle me-1"/>Consulta apenas — cadastro pelo Admin ou Portal do Condômino
              </small>
            </div>

            {loading ? (
              <div style={{textAlign:"center",padding:40}}><span className="spinner-border text-primary"/></div>
            ) : (() => {
              // Group moradores by unit
              const allItems = moradores;
              const tits = titulares;
              // Build units map
              const unitsMap = {};
              tits.forEach(t=>{
                if (!unitsMap[t.idUnidade]) unitsMap[t.idUnidade] = {
                  idUnidade:t.idUnidade, nmUnidade:t.nmUnidade||"?", bloco:t.bloco,
                  titular:t, moradores:[]
                };
              });
              allItems.forEach(m=>{
                if (!unitsMap[m.idUnidade]) unitsMap[m.idUnidade] = {
                  idUnidade:m.idUnidade, nmUnidade:m.nmUnidade||"?", bloco:m.bloco,
                  titular:null, moradores:[]
                };
                unitsMap[m.idUnidade].moradores.push(m);
              });
              const units = Object.values(unitsMap).sort((a,b)=>{
                const ba=(a.bloco||""), bb=(b.bloco||"");
                return ba.localeCompare(bb)||a.nmUnidade.localeCompare(b.nmUnidade);
              });

              return units.length===0 ? (
                <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                  <i className="bi bi-people" style={{fontSize:40,display:"block",marginBottom:8}}/>
                  {busca ? "Nenhum resultado encontrado" : "Nenhum morador cadastrado"}
                </div>
              ) : units.map(unit=>{
                const expanded = expandedUnits?.[unit.idUnidade];
                const totalMoradores = unit.moradores.length;
                return (
                  <div key={unit.idUnidade} className="card" style={{marginBottom:8,overflow:"hidden"}}>
                    {/* Unit header — always visible */}
                    <div style={{padding:"12px 16px",cursor:"pointer",
                      background:expanded?"#EFF6FF":"#fff",
                      borderBottom:expanded?"1px solid #DBEAFE":"none"}}
                      onClick={()=>setExpandedUnits(p=>({...p,[unit.idUnidade]:!expanded}))}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <div style={{width:38,height:38,borderRadius:10,background:"#1B3A6B",
                            display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                            <i className="bi bi-house-fill" style={{color:"#fff",fontSize:16}}/>
                          </div>
                          <div>
                            <div style={{fontWeight:700,fontSize:14,color:"var(--text-primary)"}}>
                              Ap. {unit.nmUnidade}{unit.bloco?` / Bl. ${unit.bloco}`:""}
                            </div>
                            {unit.titular && (
                              <div style={{fontSize:12,color:"var(--gray-600)",marginTop:1}}>
                                <span style={{fontWeight:600}}>{unit.titular.nmMorador}</span>
                                <span style={{color:"var(--gray-400)",marginLeft:6}}>· {unit.titular.tpVinculo}</span>
                                {unit.titular.telefone && <span style={{color:"var(--gray-400)",marginLeft:6}}>· {unit.titular.telefone}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          {totalMoradores>0 && (
                            <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,
                              background:"var(--bg-highlight)",color:"#1D4ED8",fontWeight:600}}>
                              {totalMoradores} morador{totalMoradores>1?"es":""}
                            </span>
                          )}
                          <i className={`bi bi-chevron-${expanded?"up":"down"}`}
                            style={{color:"var(--gray-400)",fontSize:12}}/>
                        </div>
                      </div>
                    </div>

                    {/* Moradores list — expandable */}
                    {expanded && (
                      <div style={{padding:"8px 16px 12px",background:"var(--bg-surface-alt)"}}>
                        {totalMoradores===0 ? (
                          <p style={{fontSize:12,color:"var(--gray-400)",margin:"8px 0"}}>
                            Nenhum morador adicional cadastrado
                          </p>
                        ) : unit.moradores.map((m,i)=>(
                          <div key={m.id} style={{
                            display:"flex",alignItems:"center",gap:12,
                            padding:"8px 0",
                            borderBottom:i<unit.moradores.length-1?"1px solid #E2E8F0":"none"}}>
                            <div style={{width:32,height:32,borderRadius:16,background:"#E0E7FF",
                              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                              <i className="bi bi-person-fill" style={{color:"#3730A3",fontSize:14}}/>
                            </div>
                            <div style={{flex:1}}>
                              <div style={{fontWeight:600,fontSize:13,color:"var(--text-primary)"}}>{m.nmMorador}</div>
                              <div style={{fontSize:11,color:"var(--gray-400)"}}>
                                {m.tpMorador}{m.parentesco&&m.parentesco!==m.tpMorador?` — ${m.parentesco}`:""}
                                {m.telefone&&` · ${m.telefone}`}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* ── VEÍCULOS ── */}
        {aba==="veiculos" && (
          <div>
            {/* Header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,gap:10,flexWrap:"wrap"}}>
              <h3 style={{margin:0,fontSize:16,fontWeight:700}}>Veículos</h3>
              <button className="btn btn-primary btn-sm"
                onClick={()=>{ setFormVeicEnt({placa:"",nmMotorista:"",tpAcesso:"Visitante"}); setShowVeicEnt(true); }}>
                <i className="bi bi-car-front me-1"/>Registrar Entrada
              </button>
            </div>

            {/* Dentro agora */}
            {veicRegis.filter(r=>!r.dtSaida).length>0 && (
              <div className="card" style={{padding:0,overflow:"hidden",marginBottom:14}}>
                <div style={{padding:"10px 14px",background:"var(--bg-highlight)",fontWeight:700,fontSize:13,color:"#1D4ED8"}}>
                  <i className="bi bi-car-front-fill me-2"/>Dentro agora ({veicRegis.filter(r=>!r.dtSaida).length})
                </div>
                {veicRegis.filter(r=>!r.dtSaida).map(r=>(
                  <div key={r.id} style={{padding:"10px 14px",display:"flex",justifyContent:"space-between",
                    alignItems:"center",borderBottom:"1px solid var(--border-subtle)"}}>
                    <div>
                      <span style={{fontWeight:700,fontFamily:"monospace"}}>{r.placa}</span>
                      {r.modelo && <span style={{fontSize:12,color:"var(--gray-500)",marginLeft:8}}>{r.modelo}</span>}
                      <div style={{fontSize:11,color:"var(--gray-400)"}}>{r.tpAcesso} · {r.nmMotorista||"—"} · {fmtDT(r.dtEntrada)}</div>
                    </div>
                    <button className="btn btn-outline-warning btn-sm"
                      onClick={async()=>{await portariaService.saidaVeiculo(r.id);carregarAba("veiculos");}}>
                      Saída
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Busca */}
            <div style={{marginBottom:12}}>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <div style={{position:"relative",flex:1}}>
                  <i className="bi bi-search" style={{position:"absolute",left:10,top:"50%",
                    transform:"translateY(-50%)",color:"var(--gray-400)",fontSize:13}}/>
                  <input className="form-control" style={{paddingLeft:34,fontSize:13}}
                    placeholder="🔍 Placa, modelo ou unidade (Ex: ABC-1234)"
                    value={buscaVeic}
                    onChange={e=>{
                      setBuscaVeic(e.target.value);
                      if(!e.target.value) portariaService.veiculos({idCondominio:idCondo}).then(r=>setVeiculos(r.data||[]));
                    }}
                    onKeyDown={e=>e.key==="Enter"&&portariaService.veiculos({idCondominio:idCondo, search:buscaVeic.toUpperCase().replace(/[^A-Z0-9]/g,'') || buscaVeic}).then(r=>setVeiculos(r.data||[]))}/>
                </div>
                <button className="btn btn-primary btn-sm"
                  onClick={()=>portariaService.veiculos({idCondominio:idCondo, search:buscaVeic.toUpperCase().replace(/[^A-Z0-9]/g,'') || buscaVeic}).then(r=>setVeiculos(r.data||[]))}>
                  <i className="bi bi-search"/>
                </button>
                {buscaVeic && <button className="btn btn-outline-secondary btn-sm"
                  onClick={()=>{setBuscaVeic("");portariaService.veiculos({idCondominio:idCondo}).then(r=>{setVeiculos(r.data||[]);setExpandedUnitsVeic({});});}}>
                  <i className="bi bi-x"/>
                </button>}
              </div>
              <small style={{fontSize:11,color:"var(--gray-400)",marginTop:4,display:"block"}}>
                <i className="bi bi-info-circle me-1"/>Consulta apenas — cadastro pelo Portal do Condômino
              </small>
            </div>

            {/* Agrupado por unidade */}
            {(() => {
              const unitsVeic = {};
              veiculos.forEach(v=>{
                const key = v.idUnidade||"sem-unidade";
                if (!unitsVeic[key]) unitsVeic[key] = {
                  idUnidade:v.idUnidade, nmUnidade:v.nmUnidade||"?", bloco:v.bloco, veiculos:[]
                };
                unitsVeic[key].veiculos.push(v);
              });
              const units = Object.values(unitsVeic).sort((a,b)=>{
                return (a.bloco||"").localeCompare(b.bloco||"")||a.nmUnidade.localeCompare(b.nmUnidade);
              });
              return units.length===0 ? (
                <div style={{textAlign:"center",padding:30,color:"var(--gray-400)",fontSize:13}}>
                  <i className="bi bi-car-front" style={{fontSize:36,display:"block",marginBottom:8}}/>
                  {buscaVeic?"Nenhum resultado":"Nenhum veículo cadastrado"}
                </div>
              ) : units.map(unit=>{
                const expanded = expandedUnitsVeic[unit.idUnidade]??true; // default expanded
                return (
                  <div key={unit.idUnidade} className="card" style={{marginBottom:8,overflow:"hidden"}}>
                    <div style={{padding:"10px 14px",cursor:"pointer",
                      background:expanded?"#F0FDF4":"#fff",
                      borderBottom:expanded?"1px solid #D1FAE5":"none"}}
                      onClick={()=>setExpandedUnitsVeic(p=>({...p,[unit.idUnidade]:!expanded}))}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <div style={{width:36,height:36,borderRadius:8,background:"#1B3A6B",
                            display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                            <i className="bi bi-house-fill" style={{color:"#fff",fontSize:14}}/>
                          </div>
                          <div>
                            <div style={{fontWeight:700,fontSize:13,color:"var(--text-primary)"}}>
                              Ap. {unit.nmUnidade}{unit.bloco?` / Bl. ${unit.bloco}`:""}
                            </div>
                            <div style={{fontSize:11,color:"var(--gray-400)"}}>{unit.veiculos.length} veículo{unit.veiculos.length>1?"s":""}</div>
                          </div>
                        </div>
                        <i className={`bi bi-chevron-${expanded?"up":"down"}`} style={{color:"var(--gray-400)"}}/>
                      </div>
                    </div>
                    {expanded && (
                      <div style={{padding:"6px 14px 10px"}}>
                        {unit.veiculos.map((v,i)=>(
                          <div key={v.id} style={{display:"flex",alignItems:"center",gap:12,
                            padding:"8px 0",borderBottom:i<unit.veiculos.length-1?"1px solid #F1F5F9":"none"}}>
                            {v.foto ? (
                              <img src={v.foto} alt={v.placa}
                                style={{width:90,height:68,borderRadius:8,objectFit:"cover",
                                  flexShrink:0,border:"2px solid #E2E8F0",cursor:"zoom-in"}}
                                onClick={e=>{
                                  e.stopPropagation();
                                  const overlay=document.createElement("div");
                                  overlay.style.cssText="position:fixed;inset:0;background:rgba(0,0,0,.85);z-index:99999;display:flex;align-items:center;justify-content:center;cursor:zoom-out";
                                  const img=document.createElement("img");
                                  img.src=v.foto;
                                  img.style.cssText="max-width:90vw;max-height:90vh;border-radius:10px;box-shadow:0 8px 40px rgba(0,0,0,.5)";
                                  overlay.appendChild(img);
                                  overlay.onclick=()=>document.body.removeChild(overlay);
                                  document.body.appendChild(overlay);
                                }}/>
                            ) : (
                              <div style={{width:90,height:68,borderRadius:8,background:"var(--bg-highlight)",
                                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                <i className="bi bi-car-front-fill" style={{color:"#1D4ED8",fontSize:28}}/>
                              </div>
                            )}
                            <div style={{flex:1}}>
                              <div>
                                <span style={{fontWeight:700,fontFamily:"monospace",fontSize:14,color:"#1B3A6B",
                                  letterSpacing:1}}>{v.placa}</span>
                                <span style={{fontSize:12,color:"var(--gray-500)",marginLeft:10}}>
                                  {v.tpVeiculo}{v.modelo?` · ${v.modelo}`:""}{v.cor?` · ${v.cor}`:""}
                                </span>
                              </div>
                              {v.nmProprietario && (
                                <div style={{fontSize:11,color:"var(--gray-400)",marginTop:2}}>
                                  <i className="bi bi-person me-1"/>{v.nmProprietario}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* ── PRÉ-AUTORIZADOS ── */}
        {aba==="preauth" && (
          <div>
            <h3 style={{fontSize:16,fontWeight:700,marginBottom:16}}>Visitantes Pré-autorizados para Hoje</h3>
            {preauth.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-qr-code-scan" style={{fontSize:40,display:"block",marginBottom:8}}/>
                Nenhum visitante pré-autorizado para hoje
              </div>
            ) : preauth.map(a=>(
              <div key={a.id} className="card" style={{padding:14,marginBottom:8,
                borderLeft:`4px solid ${a.usado?"#94A3B8":"#166534"}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:14}}>{a.nmVisitante}</div>
                    <div style={{fontSize:12,color:"var(--gray-400)"}}>
                      {a.tpVisita} · Un. {a.nmUnidade}
                      {a.hrInicio && ` · ${a.hrInicio} – ${a.hrFim||"?"}`}
                    </div>
                    <div style={{fontSize:11,color:"var(--gray-500)"}}>Autorizado por: {a.nmAutorizadoPor}</div>
                  </div>
                  {!a.usado && (
                    <button className="btn btn-success btn-sm"
                      onClick={async()=>{
                        try {
                          await portariaService.registrarEntrada({
                            idCondominio:idCondo,idUnidade:a.idUnidade,
                            nmVisitante:a.nmVisitante,tpVisita:a.tpVisita,
                            observacao:`Pré-autorizado por ${a.nmAutorizadoPor}`
                          });
                          carregarAba("preauth");carregarDashboard();
                          notify("success", "Entrada registrada!");
                        } catch(err){ notify("error", err.message); }
                      }}>
                      <i className="bi bi-door-open me-1"/>Liberar Entrada
                    </button>
                  )}
                  {a.usado && <span style={{fontSize:12,color:"var(--gray-400)"}}>✓ Já entrou</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── OCORRÊNCIAS DA PORTARIA ── */}
        {aba==="ocorrencias" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <h3 style={{margin:0,fontSize:16,fontWeight:700}}>Ocorrências</h3>
              <button className="btn btn-warning btn-sm"
                onClick={()=>{setFormOcorr({tpOcorrencia:"Outro",descricao:"",local:"",gravidade:"Media",idUnidade:""});setEditOcorr(null);setShowOcorr(true);}}>
                <i className="bi bi-exclamation-triangle me-1"/>Registrar
              </button>
            </div>
            {ocorrPortaria.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-check-circle" style={{fontSize:40,display:"block",marginBottom:8}}/>
                Nenhuma ocorrência registrada
              </div>
            ) : ocorrPortaria.map(o=>(
              <div key={o.id} className="card" style={{padding:14,marginBottom:8,
                borderLeft:`4px solid ${o.gravidade==="Alta"?"#991B1B":o.gravidade==="Media"?"#854D0E":"#166534"}`}}>
                <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",gap:8,marginBottom:4}}>
                      <span style={{fontWeight:700}}>{o.tpOcorrencia}</span>
                      <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,
                        background:o.gravidade==="Alta"?"#FEE2E2":o.gravidade==="Media"?"#FEF9C3":"#DCFCE7",
                        color:o.gravidade==="Alta"?"#991B1B":o.gravidade==="Media"?"#854D0E":"#166534"}}>
                        {o.gravidade}
                      </span>
                    </div>
                    <div style={{fontSize:13}}>{o.descricao}</div>
                    <div style={{fontSize:11,color:"var(--gray-400)"}}>{fmtDT(o.dtOcorrencia)}</div>
                  </div>
                  {o.status==="Aberta" && (
                    <div style={{display:"flex",gap:6,flexShrink:0}}>
                      <button className="btn btn-outline-secondary btn-sm"
                        onClick={()=>{setFormOcorr({
                          tpOcorrencia:o.tpOcorrencia,descricao:o.descricao,
                          local:o.local||"",gravidade:o.gravidade,idUnidade:o.idUnidade||""
                        });setEditOcorr(o);setShowOcorr(true);}}>
                        <i className="bi bi-pencil"/>
                      </button>
                      <button className="btn btn-outline-danger btn-sm"
                        onClick={async()=>{
                          const ok = await askConfirm({
                            titulo:"Excluir ocorrência",
                            mensagem:"Excluir esta ocorrência? A ação não pode ser desfeita.",
                            perigo:true, textoOk:"Excluir",
                          });
                          if(!ok) return;
                          try {
                            await portariaService.excluirOcorrencia(o.id);
                            carregarAba("ocorrencias");
                            notify("success","Ocorrência excluída.");
                          } catch(e){ notify("error", e.message); }
                        }}>
                        <i className="bi bi-trash3"/>
                      </button>
                      <button className="btn btn-outline-primary btn-sm"
                        onClick={async()=>{
                          try {
                            await portariaService.atualizarStatusOc(o.id,{status:"Encaminhada"});
                            carregarAba("ocorrencias");
                            notify("info","Ocorrência encaminhada.");
                          } catch(e){ notify("error", e.message); }
                        }}>
                        Encaminhar
                      </button>
                      <button className="btn btn-success btn-sm"
                        onClick={async()=>{
                          const ok = await askConfirm({
                            titulo:"Resolver ocorrência",
                            mensagem:"Marcar esta ocorrência como Resolvida?",
                            textoOk:"Resolver",
                          });
                          if(!ok) return;
                          try {
                            await portariaService.atualizarStatusOc(o.id,{status:"Resolvida"});
                            carregarAba("ocorrencias");
                            notify("success","Ocorrência resolvida.");
                          } catch(e){ notify("error", e.message); }
                        }}>
                        <i className="bi bi-check2 me-1"/>Resolver
                      </button>
                    </div>
                  )}
                  {o.status==="Encaminhada" && (
                    <div style={{display:"flex",gap:6,flexShrink:0}}>
                      <span style={{fontSize:11,padding:"3px 10px",borderRadius:8,
                        background:"var(--bg-highlight)",color:"#1D4ED8",fontWeight:600,alignSelf:"center"}}>
                        Encaminhada
                      </span>
                      <button className="btn btn-success btn-sm"
                        onClick={async()=>{
                          const ok = await askConfirm({
                            titulo:"Resolver ocorrência",
                            mensagem:"Marcar esta ocorrência como Resolvida?",
                            textoOk:"Resolver",
                          });
                          if(!ok) return;
                          try {
                            await portariaService.atualizarStatusOc(o.id,{status:"Resolvida"});
                            carregarAba("ocorrencias");
                            notify("success","Ocorrência resolvida.");
                          } catch(e){ notify("error", e.message); }
                        }}>
                        <i className="bi bi-check2 me-1"/>Resolver
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}


        {/* ── SCANNER QR ── */}
        {aba==="scanner" && (
          <div>
            <h3 style={{fontSize:16,fontWeight:700,marginBottom:16}}>Scanner QR Code</h3>
            <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:20}}>
              Escaneie o QR Code do visitante ou digite o token manualmente para validar a pré-autorização.
            </p>

            {/* Input manual */}
            <div className="card" style={{padding:20,marginBottom:16}}>
              <label style={{fontWeight:700,fontSize:13,display:"block",marginBottom:8}}>
                <i className="bi bi-keyboard me-2"/>Digitar token manualmente
              </label>
              <div style={{display:"flex",gap:8}}>
                <input className="form-control" value={scanInput}
                  onChange={e=>setScanInput(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&scanInput.trim()&&validarToken(scanInput.trim())}
                  placeholder="Cole ou digite o token aqui..."
                  style={{fontFamily:"monospace",fontSize:13}}/>
                <button className="btn btn-primary"
                  disabled={!scanInput.trim()||scanLoading}
                  onClick={()=>validarToken(scanInput.trim())}>
                  {scanLoading
                    ? <span className="spinner-border spinner-border-sm"/>
                    : <><i className="bi bi-search me-1"/>Validar</>}
                </button>
              </div>
            </div>

            {/* Camera scanner */}
            <div className="card" style={{padding:20,marginBottom:16}}>
              <label style={{fontWeight:700,fontSize:13,display:"block",marginBottom:12}}>
                <i className="bi bi-camera me-2"/>Escanear com câmera
              </label>
              {!showScanner ? (
                <button className="btn btn-outline-primary btn-sm"
                  onClick={()=>iniciarScanner()}>
                  <i className="bi bi-qr-code-scan me-2"/>Abrir câmera para escanear
                </button>
              ) : (
                <div>
                  <div id="qr-video-container"
                    style={{borderRadius:10,overflow:"hidden",background:"#000",marginBottom:8}}>
                    <video id="qr-video" autoPlay playsInline
                      style={{width:"100%",maxHeight:300,display:"block"}}/>
                  </div>
                  <button className="btn btn-outline-secondary btn-sm"
                    onClick={()=>{pararScanner();setShowScanner(false);}}>
                    <i className="bi bi-stop-circle me-1"/>Parar câmera
                  </button>
                </div>
              )}
            </div>

            {/* Resultado */}
            {scanResult && (
              <div className="card" style={{padding:20,
                borderLeft:`4px solid ${scanResult.autorizado?"#166534":"#991B1B"}`}}>
                {scanResult.autorizado ? (
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                      <div style={{width:40,height:40,borderRadius:20,background:"var(--bg-success-soft)",
                        display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <i className="bi bi-check-circle-fill" style={{color:"#166534",fontSize:20}}/>
                      </div>
                      <div>
                        <div style={{fontWeight:700,color:"#166534",fontSize:15}}>✅ Autorizado!</div>
                        <div style={{fontSize:12,color:"var(--gray-400)"}}>Visitante pode entrar</div>
                      </div>
                    </div>
                    <div style={{background:"var(--bg-success-soft)",borderRadius:8,padding:"12px 16px",marginBottom:12}}>
                      <div style={{fontWeight:700,fontSize:15}}>{scanResult.data?.nmVisitante}</div>
                      <div style={{fontSize:12,color:"var(--gray-500)",marginTop:4}}>
                        {scanResult.data?.tpVisita} · Unidade {scanResult.data?.nmUnidade||"?"}
                        · Válido até {scanResult.data?.dtValidade?new Date(scanResult.data.dtValidade+"T12:00:00").toLocaleDateString("pt-BR"):"?"}
                      </div>
                    </div>
                    <button className="btn btn-success w-100" disabled={scanResult.registrado}
                      onClick={async()=>{
                        try {
                          await portariaService.registrarEntrada({
                            idCondominio: idCondo,
                            idUnidade:    scanResult.data?.idUnidade || null,
                            nmVisitante:  scanResult.data?.nmVisitante,
                            tpVisita:     scanResult.data?.tpVisita || "Visitante",
                            observacao:   `Entrada via QR Code${scanResult.data?.nmAutorizadoPor?` — Pré-autorizado por ${scanResult.data.nmAutorizadoPor}`:""}`,
                          });
                          carregarDashboard();
                          setScanResult({...scanResult, registrado:true});
                          notify("success", "Entrada registrada via QR.");
                        } catch(e){ notify("error", e.message); }
                      }}>
                      {scanResult.registrado
                        ? <><i className="bi bi-check2 me-1"/>Entrada registrada!</>
                        : <><i className="bi bi-door-open me-1"/>Registrar Entrada</>}
                    </button>
                  </div>
                ) : (
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:40,height:40,borderRadius:20,background:"var(--bg-danger-soft)",
                      display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <i className="bi bi-x-circle-fill" style={{color:"#991B1B",fontSize:20}}/>
                    </div>
                    <div>
                      <div style={{fontWeight:700,color:"#991B1B",fontSize:15}}>❌ Não autorizado</div>
                      <div style={{fontSize:13,color:"var(--gray-500)",marginTop:2}}>{scanResult.motivo||"QR Code inválido ou expirado"}</div>
                    </div>
                  </div>
                )}
                <button className="btn btn-outline-secondary btn-sm mt-3"
                  onClick={()=>{setScanResult(null);setScanInput("");}}>
                  <i className="bi bi-arrow-left me-1"/>Novo scan
                </button>
              </div>
            )}
          </div>
        )}

        {aba==="avisos" && (
          <AvisosPortariaPanel
            idCondo={idCondo}
            unidades={unidades}
            notify={notify}
            onAtualizarDashboard={carregarDashboard}
          />
        )}

        {aba==="prestadores" && (
          <PrestadoresPortariaPanel
            idCondo={idCondo}
            unidades={unidades}
            notify={notify}
            onRegistrarEntrada={abrirEntradaPrestador}
          />
        )}

        {aba==="notificacoes" && (
          <NotificacoesPortariaPanel usuario={usuario} />
        )}

        {/* ── RELATÓRIO ── */}
        {aba==="relatorio" && (
          <div>
            <h3 style={{fontSize:16,fontWeight:700,marginBottom:16}}>Relatório de Movimentação</h3>
            <div style={{display:"flex",gap:10,marginBottom:16,alignItems:"flex-end",flexWrap:"wrap"}}>
              <div>
                <label style={{fontSize:12,fontWeight:600,display:"block",marginBottom:4}}>De</label>
                <input type="date" className="form-control form-control-sm" value={relDtIni} onChange={e=>setRelDtIni(e.target.value)}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:600,display:"block",marginBottom:4}}>Até</label>
                <input type="date" className="form-control form-control-sm" value={relDtFim} onChange={e=>setRelDtFim(e.target.value)}/>
              </div>
              <button className="btn btn-primary btn-sm" onClick={()=>carregarAba("relatorio")}>
                <i className="bi bi-search me-1"/>Gerar
              </button>
            </div>
            {relatorio && (
              <div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:12,marginBottom:20}}>
                  {[
                    {label:"Total entradas",value:relatorio.totais?.totalEntradas||0,cor:"#1D4ED8"},
                    {label:"Visitantes",    value:relatorio.totais?.visitantes||0,   cor:"#7C3AED"},
                    {label:"Prestadores",   value:relatorio.totais?.prestadores||0,  cor:"#C2410C"},
                    {label:"Entregas",      value:relatorio.totais?.entregas||0,     cor:"#166534"},
                    {label:"Encomendas",    value:relatorio.encomendas?.total||0,    cor:"#0891B2"},
                  ].map(k=>(
                    <div key={k.label} className="card" style={{padding:"14px",textAlign:"center"}}>
                      <div style={{fontSize:26,fontWeight:700,color:k.cor}}>{k.value}</div>
                      <div style={{fontSize:11,color:"var(--gray-400)"}}>{k.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

      </div>
      {/* ═══ MODAL: REGISTRAR ENTRADA ═══ */}
      {showEntrada && (
        <div className="modal-overlay" onClick={()=>setShowEntrada(false)}>
          <div className="modal-box" style={{maxWidth:500}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-person-plus me-2"/>Registrar Entrada</h5>
              <button className="btn-close" onClick={()=>setShowEntrada(false)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-8">
                  <label className="form-label">Nome do Visitante *</label>
                  <input className="form-control" value={formEntrada.nmVisitante}
                    onChange={e=>setFE(p=>({...p,nmVisitante:e.target.value}))}
                    placeholder="Nome completo" autoFocus/>
                </div>
                <div className="col-4">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={formEntrada.tpVisita}
                    onChange={e=>setFE(p=>({...p,tpVisita:e.target.value}))}>
                    {TP_VISITA.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label">Documento (RG/CPF)</label>
                  <input className="form-control" value={formEntrada.documento}
                    onChange={e=>setFE(p=>({...p,documento:e.target.value}))}/>
                </div>
                <div className="col-6">
                  <label className="form-label">Unidade Destino</label>
                  <UnidadeAutocomplete
                    unidades={unidades}
                    value={formEntrada.idUnidade}
                    onChange={(id)=>setFE(p=>({...p,idUnidade:id}))}
                    listId="dl-unidades-entrada"
                    placeholder="Ex.: 101 / Bl. A"/>
                </div>
                {formEntrada.tpVisita==="Prestador" && (
                  <div className="col-12">
                    <label className="form-label">Empresa</label>
                    <input className="form-control" value={formEntrada.empresa}
                      onChange={e=>setFE(p=>({...p,empresa:e.target.value}))}/>
                  </div>
                )}
                <div className="col-12">
                  <label className="form-label">Motivo / Observação</label>
                  <input className="form-control" value={formEntrada.motivoVisita}
                    onChange={e=>setFE(p=>({...p,motivoVisita:e.target.value}))}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowEntrada(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleRegistrarEntrada} disabled={saving}>
                {saving?<><span className="spinner-border spinner-border-sm me-1"/>Salvando...</>:<><i className="bi bi-door-open me-1"/>Confirmar Entrada</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL: NOVA ENCOMENDA ═══ */}
      {showEncomenda && (
        <div className="modal-overlay" onClick={()=>setShowEncomenda(false)}>
          <div className="modal-box" style={{maxWidth:520}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-box-seam me-2"/>Nova Encomenda</h5>
              <button className="btn-close" onClick={()=>setShowEncomenda(false)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-6">
                  <label className="form-label">Unidade *</label>
                  <UnidadeAutocomplete
                    unidades={unidades}
                    value={formEncomenda.idUnidade}
                    onChange={(id)=>setFEnc(p=>({...p,idUnidade:id}))}
                    listId="dl-unidades-encomenda"
                    placeholder="Ex.: 101 / Bl. A"
                    required/>
                </div>
                <div className="col-6">
                  <label className="form-label">Transportadora</label>
                  <input className="form-control" value={formEncomenda.transportadora}
                    onChange={e=>setFEnc(p=>({...p,transportadora:e.target.value}))}
                    placeholder="Correios, Mercado Envios..."/>
                </div>
                <div className="col-12">
                  <label className="form-label">Descrição *</label>
                  <input className="form-control" value={formEncomenda.descricao}
                    onChange={e=>setFEnc(p=>({...p,descricao:e.target.value}))}
                    placeholder="Ex: Caixa média, envelope, etc"/>
                </div>
                <div className="col-6">
                  <label className="form-label">Remetente</label>
                  <input className="form-control" value={formEncomenda.remetente}
                    onChange={e=>setFEnc(p=>({...p,remetente:e.target.value}))}/>
                </div>
                <div className="col-6">
                  <label className="form-label">Código de Rastreio</label>
                  <input className="form-control" value={formEncomenda.codigoRastreio}
                    onChange={e=>setFEnc(p=>({...p,codigoRastreio:e.target.value}))}/>
                </div>
                {/* Foto */}
                <div className="col-12">
                  <label className="form-label">Foto do Pacote</label>
                  {cameraAtiva==="encomenda" ? (
                    <div style={{textAlign:"center"}}>
                      <video ref={videoRef} autoPlay playsInline
                        style={{width:"100%",maxHeight:240,borderRadius:8,background:"#000"}}/>
                      <button className="btn btn-success mt-2" onClick={capturarFoto}>
                        <i className="bi bi-camera me-1"/>Capturar Foto
                      </button>
                    </div>
                  ) : formEncomenda.fotoEncomenda ? (
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      <img src={formEncomenda.fotoEncomenda} alt="encomenda"
                        style={{width:80,height:80,borderRadius:8,objectFit:"cover"}}/>
                      <button className="btn btn-outline-danger btn-sm"
                        onClick={()=>setFEnc(p=>({...p,fotoEncomenda:null}))}>
                        <i className="bi bi-trash3"/>
                      </button>
                    </div>
                  ) : (
                    <div style={{display:"flex",gap:8}}>
                      <button className="btn btn-outline-secondary btn-sm"
                        onClick={()=>abrirCamera("encomenda")}>
                        <i className="bi bi-camera me-1"/>Tirar Foto
                      </button>
                      <label className="btn btn-outline-secondary btn-sm mb-0">
                        <i className="bi bi-upload me-1"/>Carregar
                        <input type="file" accept="image/*" style={{display:"none"}}
                          onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>setFEnc(p=>({...p,fotoEncomenda:ev.target.result}));r.readAsDataURL(f);}}/>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowEncomenda(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleRegistrarEncomenda} disabled={saving}>
                {saving?<><span className="spinner-border spinner-border-sm me-1"/>Salvando...</>:<><i className="bi bi-box-seam me-1"/>Registrar Encomenda</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL: RETIRADA ═══ */}
      {showRetirada && retiradaItem && (
        <div className="modal-overlay" onClick={()=>setShowRetirada(false)}>
          <div className="modal-box" style={{maxWidth:480}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-check2-circle me-2"/>Retirada de Encomenda</h5>
              <button className="btn-close" onClick={()=>setShowRetirada(false)}/>
            </div>
            <div className="modal-body">
              <div style={{background:"var(--bg-surface-alt)",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:13}}>
                <strong>{retiradaItem.descricao}</strong>
                <div style={{color:"var(--gray-400)",marginTop:2}}>Unidade {retiradaItem.nmUnidade} · Chegou em {fmtDT(retiradaItem.dtChegada)}</div>
              </div>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Quem está retirando *</label>
                  {/* Moradores da unidade para seleção rápida */}
                  {(() => {
                    const moradoresUnidade = moradores.filter(m =>
                      String(m.idUnidade) === String(retiradaItem?.idUnidade)
                    );
                    return moradoresUnidade.length > 0 ? (
                      <div style={{marginBottom:8}}>
                        <div style={{fontSize:11,color:"var(--gray-400)",marginBottom:6}}>Selecionar morador cadastrado:</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                          {moradoresUnidade.map(m=>(
                            <button key={m.id} type="button"
                              style={{
                                padding:"4px 10px",borderRadius:20,fontSize:12,cursor:"pointer",
                                border:`1.5px solid ${formRetirada.nmRetiradoPor===m.nmMorador?"#1B3A6B":"#E2E8F0"}`,
                                background:formRetirada.nmRetiradoPor===m.nmMorador?"#1B3A6B":"#F8FAFC",
                                color:formRetirada.nmRetiradoPor===m.nmMorador?"#fff":"#475569",
                                fontWeight:formRetirada.nmRetiradoPor===m.nmMorador?700:400,
                              }}
                              onClick={()=>setFR(p=>({...p,nmRetiradoPor:m.nmMorador}))}>
                              {m.nmMorador}
                              {m.tpMorador!=="Proprietario"&&<span style={{fontSize:10,opacity:.7}}> · {m.tpMorador}</span>}
                            </button>
                          ))}
                        </div>
                        <div style={{fontSize:11,color:"var(--gray-400)",margin:"6px 0 4px"}}>Ou digite o nome:</div>
                      </div>
                    ) : null;
                  })()}
                  <input className="form-control" value={formRetirada.nmRetiradoPor}
                    onChange={e=>setFR(p=>({...p,nmRetiradoPor:e.target.value}))}
                    placeholder="Nome completo de quem retirou" autoFocus/>
                </div>
                <div className="col-12">
                  <label className="form-label">Foto de quem retirou</label>
                  {cameraAtiva==="retirada" ? (
                    <div style={{textAlign:"center"}}>
                      <video ref={videoRef} autoPlay playsInline
                        style={{width:"100%",maxHeight:200,borderRadius:8,background:"#000"}}/>
                      <button className="btn btn-success mt-2" onClick={capturarFoto}>
                        <i className="bi bi-camera me-1"/>Capturar Foto
                      </button>
                    </div>
                  ) : formRetirada.fotoRetirada ? (
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      <img src={formRetirada.fotoRetirada} alt="retirada"
                        style={{width:80,height:80,borderRadius:8,objectFit:"cover"}}/>
                      <button className="btn btn-outline-danger btn-sm"
                        onClick={()=>setFR(p=>({...p,fotoRetirada:null}))}>
                        <i className="bi bi-trash3"/>
                      </button>
                    </div>
                  ) : (
                    <div style={{display:"flex",gap:8}}>
                      <button className="btn btn-outline-secondary btn-sm"
                        onClick={()=>abrirCamera("retirada")}>
                        <i className="bi bi-camera me-1"/>Tirar Foto
                      </button>
                      <label className="btn btn-outline-secondary btn-sm mb-0">
                        <i className="bi bi-upload me-1"/>Carregar
                        <input type="file" accept="image/*" style={{display:"none"}}
                          onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>setFR(p=>({...p,fotoRetirada:ev.target.result}));r.readAsDataURL(f);}}/>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowRetirada(false)}>Cancelar</button>
              <button className="btn btn-success" onClick={handleRetirada} disabled={saving}>
                {saving?<><span className="spinner-border spinner-border-sm me-1"/>Salvando...</>:<><i className="bi bi-check2-circle me-1"/>Confirmar Retirada</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL: MORADOR ═══ */}
      {showMorador && (
        <div className="modal-overlay" onClick={()=>setShowMorador(false)}>
          <div className="modal-box" style={{maxWidth:480}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-person-plus me-2"/>
                {editMorador?"Editar Morador":"Novo Morador"}
              </h5>
              <button className="btn-close" onClick={()=>setShowMorador(false)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-6">
                  <label className="form-label">Unidade *</label>
                  <UnidadeAutocomplete
                    unidades={unidades}
                    value={formMorador.idUnidade}
                    onChange={(id)=>setFM(p=>({...p,idUnidade:id}))}
                    listId="dl-unidades-morador"
                    placeholder="Ex.: 101 / Bl. A"
                    required/>
                </div>
                <div className="col-6">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={formMorador.tpMorador}
                    onChange={e=>setFM(p=>({...p,tpMorador:e.target.value}))}>
                    {TP_MORADOR.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Nome Completo *</label>
                  <input className="form-control" value={formMorador.nmMorador}
                    onChange={e=>setFM(p=>({...p,nmMorador:e.target.value}))} autoFocus/>
                </div>
                <div className="col-6">
                  <label className="form-label">Parentesco</label>
                  <input className="form-control" value={formMorador.parentesco}
                    onChange={e=>setFM(p=>({...p,parentesco:e.target.value}))}
                    placeholder="Filho, Cônjuge..."/>
                </div>
                <div className="col-6">
                  <label className="form-label">Telefone</label>
                  <input className="form-control" value={formMorador.telefone}
                    onChange={e=>setFM(p=>({...p,telefone:e.target.value}))}/>
                </div>
                <div className="col-6">
                  <label className="form-label">CPF</label>
                  <input className="form-control" value={formMorador.cpf}
                    onChange={e=>setFM(p=>({...p,cpf:e.target.value}))}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowMorador(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSalvarMorador} disabled={saving}>
                {saving?<><span className="spinner-border spinner-border-sm me-1"/>Salvando...</>:"Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ═══ MODAL: ENTRADA VEÍCULO ═══ */}
      {showVeicEnt && (
        <div className="modal-overlay" onClick={()=>{setShowVeicEnt(false);setRecogVeic({status:"idle",veiculo:null});}}>
          <div className="modal-box" style={{maxWidth:460}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-car-front me-2"/>Entrada de Veículo</h5>
              <button className="btn-close" onClick={()=>{setShowVeicEnt(false);setRecogVeic({status:"idle",veiculo:null});}}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Placa *</label>
                  <input className="form-control"
                    style={{textTransform:"uppercase",fontSize:18,fontFamily:"monospace",letterSpacing:2}}
                    value={formVeicEnt.placa} autoFocus
                    onChange={e=>setFormVeicEnt(p=>({...p,placa:e.target.value.toUpperCase()}))}
                    placeholder="ABC-1234"/>
                  {/* Feedback de reconhecimento */}
                  {recogVeic.status === "searching" && (
                    <div style={{marginTop:8,padding:"8px 12px",background:"var(--bg-page)",borderRadius:8,
                      fontSize:12,color:"var(--gray-500)",display:"flex",alignItems:"center",gap:8}}>
                      <span className="spinner-border spinner-border-sm"/> Verificando cadastro…
                    </div>
                  )}
                  {recogVeic.status === "found" && recogVeic.veiculo && (
                    <div style={{marginTop:8,padding:"10px 12px",background:"#ECFDF5",border:"1px solid #A7F3D0",
                      borderRadius:8,fontSize:13,color:"#065F46"}}>
                      <div style={{fontWeight:700}}>
                        <i className="bi bi-check-circle-fill me-2"/>Veículo cadastrado
                      </div>
                      <div style={{marginTop:4}}>
                        Un. <strong>{recogVeic.veiculo.nmUnidade}{recogVeic.veiculo.bloco?` / Bl. ${recogVeic.veiculo.bloco}`:""}</strong>
                        {recogVeic.veiculo.nmProprietario ? ` — ${recogVeic.veiculo.nmProprietario}` : ""}
                      </div>
                      <div style={{fontSize:11,color:"#047857",marginTop:2}}>
                        {recogVeic.veiculo.tpVeiculo}{recogVeic.veiculo.modelo?` · ${recogVeic.veiculo.modelo}`:""}{recogVeic.veiculo.cor?` · ${recogVeic.veiculo.cor}`:""}
                      </div>
                    </div>
                  )}
                  {recogVeic.status === "not_found" && (
                    <div style={{marginTop:8,padding:"10px 12px",background:"#FEF3C7",border:"1px solid #FCD34D",
                      borderRadius:8,fontSize:13,color:"#92400E"}}>
                      <i className="bi bi-exclamation-triangle-fill me-2"/>
                      Placa <strong>não cadastrada</strong> — registrar como <strong>Visitante</strong>.
                    </div>
                  )}
                </div>
                <div className="col-6">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={formVeicEnt.tpAcesso}
                    onChange={e=>setFormVeicEnt(p=>({...p,tpAcesso:e.target.value}))}>
                    <option value="Morador">Morador</option>
                    <option value="Visitante">Visitante</option>
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label">Motorista</label>
                  <input className="form-control" value={formVeicEnt.nmMotorista}
                    onChange={e=>setFormVeicEnt(p=>({...p,nmMotorista:e.target.value}))}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>{setShowVeicEnt(false);setRecogVeic({status:"idle",veiculo:null});}}>Cancelar</button>
              <button className="btn btn-primary" disabled={saving}
                onClick={async()=>{
                  if(!formVeicEnt.placa.trim()) return notify("warning","Informe a placa.");
                  setSaving(true);
                  try {
                    await portariaService.registrarVeiculo({...formVeicEnt,idCondominio:idCondo});
                    setShowVeicEnt(false);
                    setFormVeicEnt({placa:"",nmMotorista:"",tpAcesso:"Visitante"});
                    setRecogVeic({status:"idle",veiculo:null});
                    carregarDashboard();carregarAba("veiculos");
                    notify("success","Entrada de veículo registrada.");
                  } catch(e){ notify("error", e.message); }
                  setSaving(false);
                }}>
                <i className="bi bi-car-front me-1"/>Registrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL: OCORRÊNCIA ═══ */}
      {showOcorr && (
        <div className="modal-overlay" onClick={()=>{setShowOcorr(false);setEditOcorr(null);}}>
          <div className="modal-box" style={{maxWidth:480}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-exclamation-triangle me-2"/>{editOcorr?"Editar Ocorrência":"Registrar Ocorrência"}</h5>
              <button className="btn-close" onClick={()=>{setShowOcorr(false);setEditOcorr(null);}}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-6">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={formOcorr.tpOcorrencia}
                    onChange={e=>setFormOcorr(p=>({...p,tpOcorrencia:e.target.value}))}>
                    {["Barulho","Segurança","Acesso","Dano","Outro"].map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label">Gravidade</label>
                  <select className="form-select" value={formOcorr.gravidade}
                    onChange={e=>setFormOcorr(p=>({...p,gravidade:e.target.value}))}>
                    <option value="Baixa">🟢 Baixa</option>
                    <option value="Media">🟡 Média</option>
                    <option value="Alta">🔴 Alta</option>
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label">Unidade</label>
                  <UnidadeAutocomplete
                    unidades={unidades}
                    value={formOcorr.idUnidade}
                    onChange={(id)=>setFormOcorr(p=>({...p,idUnidade:id}))}
                    listId="dl-unidades-ocorr"
                    placeholder="Opcional — Ex.: 101 / Bl. A"/>
                </div>
                <div className="col-6">
                  <label className="form-label">Local</label>
                  <input className="form-control" value={formOcorr.local}
                    onChange={e=>setFormOcorr(p=>({...p,local:e.target.value}))} placeholder="Ex: Garagem"/>
                </div>
                <div className="col-12">
                  <label className="form-label">Descrição *</label>
                  <textarea className="form-control" rows={3} value={formOcorr.descricao}
                    onChange={e=>setFormOcorr(p=>({...p,descricao:e.target.value}))}
                    placeholder="Descreva o ocorrido..."/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>{setShowOcorr(false);setEditOcorr(null);}}>Cancelar</button>
              <button className={`btn ${editOcorr?"btn-primary":"btn-warning"}`} disabled={saving}
                onClick={async()=>{
                  if(!formOcorr.descricao.trim()) return notify("warning","Informe a descrição.");
                  setSaving(true);
                  try {
                    if (editOcorr) {
                      await portariaService.editarOcorrencia(editOcorr.id, formOcorr);
                    } else {
                      await portariaService.criarOcorrenciaPort({...formOcorr,idCondominio:idCondo});
                    }
                    setShowOcorr(false); setEditOcorr(null);
                    setFormOcorr({tpOcorrencia:"Outro",descricao:"",local:"",gravidade:"Media",idUnidade:""});
                    carregarAba("ocorrencias");
                    notify("success", editOcorr ? "Ocorrência atualizada." : "Ocorrência registrada.");
                  } catch(e){ notify("error", e.message); }
                  setSaving(false);
                }}>
                <i className={`bi ${editOcorr?"bi-check2":"bi-exclamation-triangle"} me-1`}/>
                {editOcorr ? "Salvar Alterações" : "Registrar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Editar Encomenda ── */}
      {editEnc && (
        <div className="modal-overlay" onClick={()=>setEditEnc(null)}>
          <div className="modal-box" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-pencil me-2"/>Editar Encomenda</h5>
              <button className="btn-close" onClick={()=>setEditEnc(null)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Descrição *</label>
                  <input className="form-control" value={editEnc.descricao||""}
                    onChange={e=>setEditEnc(p=>({...p,descricao:e.target.value}))}/>
                </div>
                <div className="col-12">
                  <label className="form-label">Remetente</label>
                  <input className="form-control" value={editEnc.remetente||""}
                    onChange={e=>setEditEnc(p=>({...p,remetente:e.target.value}))}/>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Transportadora</label>
                  <input className="form-control" value={editEnc.transportadora||""}
                    onChange={e=>setEditEnc(p=>({...p,transportadora:e.target.value}))}/>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Cód. Rastreio</label>
                  <input className="form-control" value={editEnc.codigoRastreio||""}
                    onChange={e=>setEditEnc(p=>({...p,codigoRastreio:e.target.value}))}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={()=>setEditEnc(null)}>Cancelar</button>
              <button className="btn btn-primary btn-sm" onClick={async()=>{
                try {
                  await portariaService.editarEncomenda(editEnc.id, editEnc);
                  setEditEnc(null); carregarAba("encomendas");
                  notify("success", "Encomenda atualizada.");
                } catch(err){ notify("error", err.message); }
              }}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Editar Registro de Entrada ── */}
      {editReg && (
        <div className="modal-overlay" onClick={()=>setEditReg(null)}>
          <div className="modal-box" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-pencil me-2"/>Editar Registro de Entrada</h5>
              <button className="btn-close" onClick={()=>setEditReg(null)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Nome do Visitante *</label>
                  <input className="form-control" value={editReg.nmVisitante||""}
                    onChange={e=>setEditReg(p=>({...p,nmVisitante:e.target.value}))}/>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={editReg.tpVisita||"Visitante"}
                    onChange={e=>setEditReg(p=>({...p,tpVisita:e.target.value}))}>
                    {TP_VISITA.map(t=>
                      <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Documento</label>
                  <input className="form-control" value={editReg.documento||""}
                    onChange={e=>setEditReg(p=>({...p,documento:e.target.value}))}/>
                </div>
                <div className="col-12">
                  <label className="form-label">Motivo</label>
                  <input className="form-control" value={editReg.motivoVisita||""}
                    onChange={e=>setEditReg(p=>({...p,motivoVisita:e.target.value}))}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={()=>setEditReg(null)}>Cancelar</button>
              <button className="btn btn-primary btn-sm" onClick={async()=>{
                try {
                  await portariaService.editarRegistro(editReg.id, editReg);
                  setEditReg(null); carregarAba("entradas");
                  notify("success", "Registro atualizado.");
                } catch(err){ notify("error", err.message); }
              }}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Banner global de feedback ── */}
      {banner && (
        <div style={{position:"fixed",top:16,right:16,zIndex:9999,maxWidth:480}}>
          <Banner tipo={banner.tipo} onClose={()=>setBanner(null)}>{banner.msg}</Banner>
        </div>
      )}

      {/* ── Modal de confirmação ── */}
      {confirmacao && (
        <ConfirmModal
          titulo={confirmacao.titulo}
          mensagem={confirmacao.mensagem}
          perigo={confirmacao.perigo}
          textoOk={confirmacao.textoOk}
          onConfirm={confirmacao.onConfirm}
          onCancel={confirmacao.onCancel}
        />
      )}

      {/* ── Modal de motivo (substitui window.prompt) ── */}
      {motivoModal && (
        <div className="modal fade show d-block" style={{background:"rgba(0,0,0,.5)"}}
          role="dialog" aria-modal="true" onClick={()=>fecharMotivo(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e=>e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{motivoModal.titulo}</h5>
                <button type="button" className="btn-close" onClick={()=>fecharMotivo(null)} aria-label="Fechar"/>
              </div>
              <div className="modal-body">
                <label className="form-label">{motivoModal.label}</label>
                <textarea className="form-control" rows={3} autoFocus
                  value={motivoTexto}
                  onChange={e=>setMotivoTexto(e.target.value)}
                  placeholder="Digite aqui..."/>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>fecharMotivo(null)}>Cancelar</button>
                <button type="button" className="btn btn-danger" onClick={()=>fecharMotivo(motivoTexto || "")}>
                  {motivoModal.textoOk}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
